import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { selectCarteiraDocumentos, voltarParaLista, voltarParaListaAposExclusao } from '../carteiraDocumentosSlice';
import { obterDownloadUrl, excluirDocumento } from '../../../api/carteiraDocumentosAPI';
import ImagemInterativa from '../Components/ImagemInterativa';
import PdfInterativo from '../Components/PdfInterativo';
import styles from './VisualizarDocumento.module.css';

interface VisualizarDocumentoProps {
  ocultarExcluir?: boolean;
}

const VisualizarDocumento: React.FC<VisualizarDocumentoProps> = ({ ocultarExcluir = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  const dispatch = useAppDispatch();
  const dados = useAppSelector(selectCarteiraDocumentos);

  useEffect(() => {
    if (!dados.referenciaDocumento) return;
    setLoadingUrl(true);
    obterDownloadUrl(dados.referenciaDocumento)
      .then(({ url }) => {
        setDownloadUrl(url);
        const ext = dados.referenciaDocumento!.split('.').pop()?.toLowerCase();
        setIsPdf(ext === 'pdf');
      })
      .catch((err) => console.error('[VisualizarDocumento] erro ao obter download url:', err))
      .finally(() => setLoadingUrl(false));
  }, [dados.referenciaDocumento]);

  const confirmarExclusao = async () => {
    if (!dados.idDocumento) return;
    setExcluindo(true);
    try {
      await excluirDocumento(dados.idDocumento);
      setShowModal(false);
      dispatch(voltarParaListaAposExclusao());
    } catch (err: any) {
      alert(err?.message || 'Erro ao excluir o documento. Tente novamente.');
    } finally {
      setExcluindo(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.integranteNome}>{dados.integranteNome}</h2>
        <p className={styles.tipoDocumento}>{dados.nomeDocumento}</p>
      </div>

      <div className={styles.documentoContainer}>
        {loadingUrl && (
          <div className={styles.documentoImagem}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: 24, color: '#1351b4' }}></i>
          </div>
        )}
        {!loadingUrl && downloadUrl && (
          isPdf ? (
            <PdfInterativo src={downloadUrl} />
          ) : (
            <ImagemInterativa src={downloadUrl} alt="Documento" />
          )
        )}
        {!loadingUrl && !downloadUrl && (
          <div className={styles.documentoImagem}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: 24, color: '#f44336' }}></i>
          </div>
        )}
      </div>

      {!ocultarExcluir && (
        <div className={styles.acoes}>
          <button 
            className={styles.botaoExcluir}
            onClick={() => setShowModal(true)}
          >
            <i className="fas fa-trash-alt"></i>
            Excluir
          </button>
        </div>
      )}

      {!ocultarExcluir && showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p className={styles.modalTexto}>
              Tem certeza que deseja excluir este documento?
            </p>
            <div className={styles.modalBotoes}>
              <button className={styles.botaoConfirmar} onClick={confirmarExclusao} disabled={excluindo}>
                {excluindo ? 'Excluindo...' : 'Sim, quero excluir'}
              </button>
              <button className={styles.botaoCancelar} onClick={() => setShowModal(false)}>
                Não
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualizarDocumento;
